/* import __COLOCATED_TEMPLATE__ from './live-person-connection.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type LivePersonConfiguration from 'embercom/models/standalone/live-person-configuration';
import type { ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import { type PulseAccordion } from 'glint/pulse';

interface Args {
  accordion: PulseAccordion;
  livePersonConfig: LivePersonConfiguration;
}

export default class StandaloneApiLivePersonConnection extends Component<Args> {
  @service declare intl: IntlService;

  @tracked isConfigDrawerOpen = false;
  @tracked isAdditionalSettingsDrawerOpen = false;

  get configurationCard(): ConnectionOption {
    return {
      value: 'liveperson-config',
      label: this.intl.t('standalone.deploy.api.live_person.configuration_card.title'),
      description: this.intl.t('standalone.deploy.api.live_person.configuration_card.description'),
      isConnected: !this.args.livePersonConfig?.isPendingSetup,
      isAuthFailed: false,
      stampConnected: this.intl.t('standalone.deploy.api.live_person.connected'),
      manage: () => {
        this.openConfigDrawer();
      },
      connect: () => {
        this.openConfigDrawer();
      },
    };
  }

  get additionalSettingsCard(): ConnectionOption {
    let isLivePersonConfigured = !this.args.livePersonConfig?.isPendingSetup;

    return {
      value: 'liveperson-tokens',
      label: this.intl.t('standalone.deploy.api.live_person.token_settings_card.title'),
      description: this.intl.t('standalone.deploy.api.live_person.token_settings_card.description'),
      isConnected: true,
      isAuthFailed: false,
      hideStamp: true,
      buttonConnected: 'Manage ›',
      buttonDisabledTooltip: isLivePersonConfigured
        ? undefined
        : this.intl.t('standalone.deploy.api.live_person.token_settings_card.disabled_tooltip'),
      manage: () => {
        this.openAdditionalSettingsDrawer();
      },
      connect: () => {
        this.openAdditionalSettingsDrawer();
      },
    };
  }

  @action openConfigDrawer() {
    this.isConfigDrawerOpen = true;
  }

  @action closeConfigDrawer() {
    this.isConfigDrawerOpen = false;
  }

  @action closeConfigOnSave() {
    this.isConfigDrawerOpen = false;
  }

  @action openAdditionalSettingsDrawer() {
    this.isAdditionalSettingsDrawerOpen = true;
  }

  @action closeAdditionalSettingsDrawer() {
    this.isAdditionalSettingsDrawerOpen = false;
  }

  @action closeAdditionalSettingsOnSave() {
    this.isAdditionalSettingsDrawerOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::CustomChannelApi::LivePersonConnection': typeof StandaloneApiLivePersonConnection;
    'standalone/custom-channel-api/live-person-connection': typeof StandaloneApiLivePersonConnection;
  }
}
